import { formatCountdown, prefixedKey } from '../composables/useAutomationBadge';
import type { DeviceAutomationConfig, DeviceAutomationSummary } from '../stores/deviceAutomations';
import { tt } from './consoleText';
import type { Bilingual } from '../config/bilingual';
import { field, wireArray } from './wire';

export const SWITCH_WIDGET_AUTOMATION_TYPES = ['switch_off_timer', 'time_switch', 'light_motion_switch'] as const;

export type SwitchWidgetAutomationType = (typeof SWITCH_WIDGET_AUTOMATION_TYPES)[number];

/**
 * A schedule edge: either a fixed clock time ("HH:MM") or a solar anchor
 * (sunrise/sunset with an optional offset in minutes).
 */
export interface TimeSwitchAnchor {
  type: 'time' | 'sunrise' | 'sunset';
  time?: string;
  offset_minutes?: number;
}

export type TimeSwitchTime = string | TimeSwitchAnchor;

/** Sunrise/sunset for a given day (ISO string fields, e.g. from useSolar). */
export interface SunTimesLike {
  sunrise: Date;
  sunset: Date;
}

export interface TimeSwitchSchedulePair {
  on_time: TimeSwitchTime;
  off_time: TimeSwitchTime;
}

export interface TimeSwitchScheduleEntry extends TimeSwitchSchedulePair {
  day?: number;
}

export interface WeeklyScheduleDay {
  day: number;
  label: Bilingual;
  shortLabel: Bilingual;
  enabled: boolean;
  pairs: TimeSwitchSchedulePair[];
}

export interface SwitchAutomationDisplay {
  automationId: string;
  automationType: SwitchWidgetAutomationType;
  name: string;
  icon: string;
  enabled: boolean;
  isActive: boolean;
  countdownText: string;
  progressRatio: number | null;
  targetIso: string | null;
  stateLabel: string;
  detailLabel: string;
  config: DeviceAutomationConfig;
  /** light_motion_switch: motion sensor currently detects motion */
  motionActive?: boolean;
  /** light_motion_switch: seconds remaining on the off-countdown */
  remainingSeconds?: number | null;
  /** light_motion_switch: full delay_seconds of the off-countdown */
  totalSeconds?: number | null;
}

interface ScheduleTransition {
  at: Date;
  action: 'on' | 'off';
  entryDay: number;
  onTime: TimeSwitchAnchor;
  offTime: TimeSwitchAnchor;
}

const DEFAULT_DAILY_ON_TIME = '08:00';
const DEFAULT_DAILY_OFF_TIME = '22:00';
const MINUTES_PER_DAY = 24 * 60;
const MINUTES_PER_WEEK = MINUTES_PER_DAY * 7;

// Fallback sun times (validation / no live solar data)
const FALLBACK_SUNRISE_MINUTES = 6 * 60; // 06:00
const FALLBACK_SUNSET_MINUTES = 20 * 60; // 20:00

/**
 * A schedule edge's wording. The table carries both languages (`Bilingual`)
 * and the accessor/consumer picks; `value` is what the config stores.
 */
export const TIME_ANCHOR_TYPES = [
  { value: 'time', label: { en: 'Clock time', sl: 'Ura' } },
  { value: 'sunrise', label: { en: 'Sunrise', sl: 'Sončni vzhod' } },
  { value: 'sunset', label: { en: 'Sunset', sl: 'Sončni zahod' } },
] as const satisfies ReadonlyArray<{ value: TimeSwitchAnchor['type']; label: Bilingual }>;

/** The week, as the schedule editor's day buttons word it. */
export const WEEKDAY_OPTIONS = [
  { day: 0, label: { en: 'Sunday', sl: 'Nedelja' }, shortLabel: { en: 'Sun', sl: 'Ned' } },
  { day: 1, label: { en: 'Monday', sl: 'Ponedeljek' }, shortLabel: { en: 'Mon', sl: 'Pon' } },
  { day: 2, label: { en: 'Tuesday', sl: 'Torek' }, shortLabel: { en: 'Tue', sl: 'Tor' } },
  { day: 3, label: { en: 'Wednesday', sl: 'Sreda' }, shortLabel: { en: 'Wed', sl: 'Sre' } },
  { day: 4, label: { en: 'Thursday', sl: 'Četrtek' }, shortLabel: { en: 'Thu', sl: 'Čet' } },
  { day: 5, label: { en: 'Friday', sl: 'Petek' }, shortLabel: { en: 'Fri', sl: 'Pet' } },
  { day: 6, label: { en: 'Saturday', sl: 'Sobota' }, shortLabel: { en: 'Sat', sl: 'Sob' } },
] as const satisfies ReadonlyArray<{ day: number; label: Bilingual; shortLabel: Bilingual }>;

export function isTimeSwitchAnchor(value: unknown): value is TimeSwitchAnchor {
  return !!value && typeof value === 'object' && !Array.isArray(value) && 'type' in (value as Record<string, unknown>);
}

export function isTimeSwitchSolar(value: TimeSwitchTime | undefined): boolean {
  return isTimeSwitchAnchor(value) && (value.type === 'sunrise' || value.type === 'sunset');
}

/** Convert an ISO-string sun-times snapshot (from useSolar) to Date form. */
export function toSunTimesLike(
  times: { sunrise?: string | null; sunset?: string | null } | null | undefined,
): SunTimesLike | null {
  if (!times) return null;
  const sunrise = new Date(times.sunrise ?? '');
  const sunset = new Date(times.sunset ?? '');
  if (Number.isNaN(sunrise.getTime()) || Number.isNaN(sunset.getTime())) {
    return null;
  }
  return { sunrise, sunset };
}

export function isSwitchWidgetAutomationType(automationType: string): automationType is SwitchWidgetAutomationType {
  return (SWITCH_WIDGET_AUTOMATION_TYPES as readonly string[]).includes(automationType);
}

export function getSwitchWidgetAutomationSummaries(automations: DeviceAutomationSummary[]): DeviceAutomationSummary[] {
  return automations.filter((automation) => isSwitchWidgetAutomationType(automation.automationType));
}

export function formatDurationLabel(totalSeconds: number | null | undefined): string {
  if (!totalSeconds || totalSeconds <= 0) return tt('0m', '0 min');

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  // The unit suffixes are copy too: a Slovene reader reads a bare "m" as metres,
  // so the Slovenian half spells the minute; English keeps its exact old shape.
  const h = tt('h', 'h');
  const m = tt('m', 'min');
  const s = tt('s', 's');

  if (hours > 0) {
    return minutes > 0 ? `${hours}${h} ${minutes}${m}` : `${hours}${h}`;
  }

  if (minutes > 0) {
    return seconds > 0 ? `${minutes}${m} ${seconds}${s}` : `${minutes}${m}`;
  }

  return `${seconds}${s}`;
}

/**
 * The schedule readers below take the automation's config bag as `unknown`-valued
 * JSON (`DeviceAutomationConfig`): a legacy payload whose keys the hub's own
 * editor writes, so each entry is checked (`Array.isArray`, `field()`) before it
 * is used rather than read off a widened bag.
 */
export function getDailySchedulePairs(config: Record<string, unknown>): TimeSwitchSchedulePair[] {
  const dailySchedule = wireArray(config.daily_schedule);
  if (dailySchedule) {
    return dailySchedule
      .map((entry) => ({
        on_time: normalizeAnchor(field(entry, 'on_time'), DEFAULT_DAILY_ON_TIME),
        off_time: normalizeAnchor(field(entry, 'off_time'), DEFAULT_DAILY_OFF_TIME),
      }))
      .sort(comparePair);
  }

  const hasLegacyValues = config.daily_on_time !== undefined || config.daily_off_time !== undefined;
  if (!hasLegacyValues) {
    return [{ on_time: DEFAULT_DAILY_ON_TIME, off_time: DEFAULT_DAILY_OFF_TIME }];
  }

  return [
    {
      on_time: normalizeAnchor(config.daily_on_time, DEFAULT_DAILY_ON_TIME),
      off_time: normalizeAnchor(config.daily_off_time, DEFAULT_DAILY_OFF_TIME),
    },
  ];
}

export function buildDailyScheduleFromPairs(pairs: TimeSwitchSchedulePair[]): TimeSwitchSchedulePair[] {
  return pairs
    .map((pair) => ({
      on_time: normalizeAnchor(pair.on_time, DEFAULT_DAILY_ON_TIME),
      off_time: normalizeAnchor(pair.off_time, DEFAULT_DAILY_OFF_TIME),
    }))
    .sort(comparePair);
}

export function getWeeklyScheduleDays(config: Record<string, unknown>): WeeklyScheduleDay[] {
  const entryByDay = new Map<number, TimeSwitchSchedulePair[]>();

  for (const entry of getWeeklyScheduleEntries(config)) {
    if (typeof entry.day !== 'number') continue;
    const pairs = entryByDay.get(entry.day) ?? [];
    pairs.push({
      on_time: entry.on_time,
      off_time: entry.off_time,
    });
    entryByDay.set(entry.day, pairs);
  }

  return WEEKDAY_OPTIONS.map((weekday) => {
    const pairs = (entryByDay.get(weekday.day) ?? []).sort(comparePair);
    return {
      day: weekday.day,
      label: weekday.label,
      shortLabel: weekday.shortLabel,
      enabled: pairs.length > 0,
      pairs,
    };
  });
}

export function buildWeeklyScheduleFromDays(days: WeeklyScheduleDay[]): TimeSwitchScheduleEntry[] {
  return days
    .flatMap((day) =>
      (day.enabled ? day.pairs : []).map((pair) => ({
        day: day.day,
        on_time: normalizeAnchor(pair.on_time, DEFAULT_DAILY_ON_TIME),
        off_time: normalizeAnchor(pair.off_time, DEFAULT_DAILY_OFF_TIME),
      })),
    )
    .sort(compareEntry);
}

export function getTimeSwitchScheduleErrors(config: Record<string, unknown>): string[] {
  const scheduleMode = config.schedule_mode === 'weekly' ? 'weekly' : 'daily';
  const entries =
    scheduleMode === 'weekly'
      ? getWeeklyScheduleEntries(config).map((entry) => {
          const onAnchor = normalizeAnchor(entry.on_time, DEFAULT_DAILY_ON_TIME);
          const offAnchor = normalizeAnchor(entry.off_time, DEFAULT_DAILY_OFF_TIME);
          const entryWeekday = WEEKDAY_OPTIONS.find((weekday) => weekday.day === entry.day);
          const entryDayLabel = entryWeekday
            ? tt(entryWeekday.shortLabel.en, entryWeekday.shortLabel.sl)
            : tt('Day', 'Dan');
          return {
            day: entry.day ?? 0,
            onAnchor,
            offAnchor,
            onMinutes: resolveAnchorMinutes(onAnchor, null),
            offMinutes: resolveAnchorMinutes(offAnchor, null),
            label: `${entryDayLabel} ${formatAnchorLabel(onAnchor)}-${formatAnchorLabel(offAnchor)}`,
          };
        })
      : expandDailyPairs(getDailySchedulePairs(config)).map((entry) => {
          const onAnchor = normalizeAnchor(entry.on_time, DEFAULT_DAILY_ON_TIME);
          const offAnchor = normalizeAnchor(entry.off_time, DEFAULT_DAILY_OFF_TIME);
          return {
            day: entry.day ?? 0,
            onAnchor,
            offAnchor,
            onMinutes: resolveAnchorMinutes(onAnchor, null),
            offMinutes: resolveAnchorMinutes(offAnchor, null),
            label: `${formatAnchorLabel(onAnchor)}-${formatAnchorLabel(offAnchor)}`,
          };
        });

  const windows = entries.map((entry) => toWeeklyWindow(entry.day, entry.onMinutes, entry.offMinutes, entry.label));
  const shiftedWindows = windows.map((window) => ({
    ...window,
    start: window.start + MINUTES_PER_WEEK,
    end: window.end + MINUTES_PER_WEEK,
  }));
  const sortedWindows = [...windows, ...shiftedWindows].sort(
    (left, right) => left.start - right.start || left.end - right.end,
  );

  const errors = new Set<string>();
  for (let index = 1; index < sortedWindows.length; index += 1) {
    const previous = sortedWindows[index - 1];
    const current = sortedWindows[index];
    if (current.start < previous.end) {
      errors.add(
        tt(
          `Schedule entries overlap: ${previous.label} conflicts with ${current.label}`,
          `Termini se prekrivajo: ${previous.label} se prekriva z ${current.label}`,
        ),
      );
    }
  }

  return [...errors];
}

export function buildSwitchAutomationDisplay(
  automation: DeviceAutomationSummary,
  currentValues: Record<string, unknown>,
  now = new Date(),
  sunTimes?: SunTimesLike | null,
): SwitchAutomationDisplay | null {
  if (!isSwitchWidgetAutomationType(automation.automationType)) {
    return null;
  }

  if (automation.automationType === 'switch_off_timer') {
    return buildSwitchOffTimerDisplay(automation, currentValues, now);
  }

  if (automation.automationType === 'light_motion_switch') {
    return buildLightMotionSwitchDisplay(automation, currentValues);
  }

  return buildTimeSwitchDisplay(automation, currentValues, now, sunTimes);
}

export function getTimeSwitchEntries(config: Record<string, unknown>): TimeSwitchScheduleEntry[] {
  if (config.schedule_mode === 'weekly') {
    return getWeeklyScheduleEntries(config);
  }

  return expandDailyPairs(getDailySchedulePairs(config));
}

export function getTimeSwitchPhaseWindow(
  config: Record<string, unknown>,
  now = new Date(),
  sunTimes?: SunTimesLike | null,
) {
  const transitions = collectTransitions(config, now, sunTimes);
  if (transitions.length === 0) {
    return {
      previous: null,
      next: null,
      isActive: false,
      progressRatio: null,
    };
  }

  const nowMs = now.getTime();
  const previous = [...transitions].reverse().find((transition) => transition.at.getTime() <= nowMs) ?? null;
  const next = transitions.find((transition) => transition.at.getTime() > nowMs) ?? null;

  const isActive = previous?.action === 'on';
  let progressRatio: number | null = null;

  if (previous && next) {
    const segmentDuration = next.at.getTime() - previous.at.getTime();
    if (segmentDuration > 0) {
      const remaining = next.at.getTime() - nowMs;
      progressRatio = clamp01(remaining / segmentDuration);
    }
  }

  return {
    previous,
    next,
    isActive,
    progressRatio,
  };
}

function buildSwitchOffTimerDisplay(
  automation: DeviceAutomationSummary,
  currentValues: Record<string, unknown>,
  now: Date,
): SwitchAutomationDisplay {
  const isActive = automation.enabled && getBooleanState(currentValues, automation.automationType, 'timer_active');
  const targetIso = getStringState(currentValues, automation.automationType, 'scheduled_off_at');
  const remainingMs = targetIso ? new Date(targetIso).getTime() - now.getTime() : null;
  const totalDurationMs = Math.max(0, Number(automation.config?.on_duration_seconds ?? 0)) * 1000;
  const countdownText = isActive && remainingMs && remainingMs > 0 ? formatCountdown(remainingMs) : '';
  const progressRatio =
    isActive && remainingMs !== null && totalDurationMs > 0 ? clamp01(remainingMs / totalDurationMs) : null;

  return {
    automationId: automation.id,
    automationType: 'switch_off_timer',
    name: automation.name,
    icon: automation.icon,
    enabled: automation.enabled,
    isActive,
    countdownText,
    progressRatio,
    targetIso,
    stateLabel: isActive ? tt('Timer running', 'Časovnik teče') : tt('Timer ready', 'Časovnik pripravljen'),
    detailLabel: isActive
      ? tt('Switches off automatically', 'Samodejno se izklopi')
      : tt(
          `Saved duration ${formatDurationLabel(Number(automation.config?.on_duration_seconds ?? 0))}`,
          `Shranjena dolžina ${formatDurationLabel(Number(automation.config?.on_duration_seconds ?? 0))}`,
        ),
    config: automation.config ?? {},
  };
}

/**
 * Light Motion Switch: show a walking-man while motion is active and a live
 * countdown ring while the off-timer runs. Returns null when idle so the
 * widget falls back to any other bound automation instead of masking it.
 */
function buildLightMotionSwitchDisplay(
  automation: DeviceAutomationSummary,
  currentValues: Record<string, unknown>,
): SwitchAutomationDisplay | null {
  if (!automation.enabled) return null;

  const motionActive = getBooleanState(currentValues, automation.automationType, 'motion_active');
  const timerActive = getBooleanState(currentValues, automation.automationType, 'timer_active');
  const rawRemaining = getNumberState(currentValues, automation.automationType, 'time_remaining_seconds');
  const remainingSeconds = rawRemaining !== null && rawRemaining > 0 ? rawRemaining : null;

  // timer_active can arrive a socket tick before the seconds value updates
  const timerRunning = timerActive && (remainingSeconds === null || remainingSeconds > 0);

  const totalSeconds = Math.max(0, Number(automation.config?.delay_seconds ?? 0));

  // Returned even when idle so the automation panel can always offer its
  // off-delay presets; the switch widget indicator hides it while idle.
  return {
    automationId: automation.id,
    automationType: 'light_motion_switch',
    name: automation.name,
    icon: automation.icon,
    enabled: automation.enabled,
    isActive: motionActive || timerRunning,
    countdownText: timerRunning && remainingSeconds !== null ? formatCountdown(remainingSeconds * 1000) : '',
    progressRatio:
      timerRunning && remainingSeconds !== null && totalSeconds > 0 ? clamp01(remainingSeconds / totalSeconds) : null,
    targetIso: null,
    stateLabel: motionActive
      ? tt('Motion detected', 'Zaznano gibanje')
      : timerRunning
        ? tt('Timer running', 'Časovnik teče')
        : tt('Motion ready', 'Pripravljeno na gibanje'),
    detailLabel: motionActive
      ? tt('Light on while motion continues', 'Luč sveti, dokler je zaznano gibanje')
      : timerRunning
        ? tt('Switches off automatically', 'Samodejno se izklopi')
        : tt(
            `Off delay ${formatDurationLabel(totalSeconds)}`,
            `Zakasnitev izklopa ${formatDurationLabel(totalSeconds)}`,
          ),
    config: automation.config ?? {},
    motionActive,
    remainingSeconds,
    totalSeconds: totalSeconds > 0 ? totalSeconds : null,
  };
}

function buildTimeSwitchDisplay(
  automation: DeviceAutomationSummary,
  currentValues: Record<string, unknown>,
  now: Date,
  sunTimes?: SunTimesLike | null,
): SwitchAutomationDisplay {
  const phase = getTimeSwitchPhaseWindow(automation.config ?? {}, now, sunTimes);
  const liveActive = getNullableBooleanState(currentValues, automation.automationType, 'schedule_active');
  const isActive = automation.enabled && (liveActive ?? phase.isActive);
  const liveTargetIso = getStringState(currentValues, automation.automationType, 'next_switch_at');
  const targetIso = liveTargetIso ?? phase.next?.at.toISOString() ?? null;
  const remainingMs = targetIso ? new Date(targetIso).getTime() - now.getTime() : null;

  return {
    automationId: automation.id,
    automationType: 'time_switch',
    name: automation.name,
    icon: automation.icon,
    enabled: automation.enabled,
    isActive,
    countdownText: remainingMs && remainingMs > 0 ? formatCountdown(remainingMs) : '',
    progressRatio: phase.progressRatio,
    targetIso,
    stateLabel: isActive ? tt('Scheduled on', 'Vklopljeno po urniku') : tt('Next schedule', 'Naslednji termin'),
    detailLabel: buildTimeSwitchScheduleLabel(automation.config ?? {}, phase.next ?? phase.previous),
    config: automation.config ?? {},
  };
}

function buildTimeSwitchScheduleLabel(config: Record<string, unknown>, transition: ScheduleTransition | null): string {
  if (!transition) {
    return tt('No schedule configured', 'Urnik ni nastavljen');
  }

  const transitionWeekday = WEEKDAY_OPTIONS.find((weekday) => weekday.day === transition.entryDay);
  const dayPrefix =
    config.schedule_mode === 'weekly' && transitionWeekday
      ? `${tt(transitionWeekday.shortLabel.en, transitionWeekday.shortLabel.sl)} `
      : '';

  const onLabel = formatAnchorLabel(transition.onTime);
  const offLabel = formatAnchorLabel(transition.offTime);
  return tt(`${dayPrefix}${onLabel} ON, ${offLabel} OFF`, `${dayPrefix}${onLabel} VKLOP, ${offLabel} IZKLOP`);
}

function collectTransitions(
  config: Record<string, unknown>,
  now: Date,
  sunTimes?: SunTimesLike | null,
): ScheduleTransition[] {
  const entries = getTimeSwitchEntries(config);
  const entriesByDay = new Map<number, TimeSwitchScheduleEntry[]>();

  for (const entry of entries) {
    if (typeof entry.day !== 'number') continue;
    const dayEntries = entriesByDay.get(entry.day) ?? [];
    dayEntries.push(entry);
    entriesByDay.set(entry.day, dayEntries);
  }

  const transitions: ScheduleTransition[] = [];

  for (let dayOffset = -8; dayOffset <= 8; dayOffset += 1) {
    const dayBase = new Date(now);
    dayBase.setHours(0, 0, 0, 0);
    dayBase.setDate(dayBase.getDate() + dayOffset);

    const daySun = sunTimes ? shiftSunTimes(sunTimes, dayOffset) : null;

    const dayEntries = entriesByDay.get(dayBase.getDay()) ?? [];
    for (const entry of dayEntries) {
      const entryDay = typeof entry.day === 'number' ? entry.day : dayBase.getDay();
      const onAnchor = normalizeAnchor(entry.on_time, DEFAULT_DAILY_ON_TIME);
      const offAnchor = normalizeAnchor(entry.off_time, DEFAULT_DAILY_OFF_TIME);
      const onAt = resolveAnchorDate(onAnchor, dayBase, daySun);
      const offAt = resolveAnchorDate(offAnchor, dayBase, daySun);

      if (offAt.getTime() <= onAt.getTime()) {
        offAt.setDate(offAt.getDate() + 1);
      }

      transitions.push({
        at: onAt,
        action: 'on',
        entryDay,
        onTime: onAnchor,
        offTime: offAnchor,
      });
      transitions.push({
        at: offAt,
        action: 'off',
        entryDay,
        onTime: onAnchor,
        offTime: offAnchor,
      });
    }
  }

  return transitions.sort((left, right) => left.at.getTime() - right.at.getTime());
}

function buildDate(base: Date, hhmm: string): Date {
  const [hours, minutes] = normalizeTime(hhmm, DEFAULT_DAILY_ON_TIME).split(':').map(Number);
  const date = new Date(base);
  date.setHours(hours || 0, minutes || 0, 0, 0);
  return date;
}

function buildDateFromMinutes(base: Date, minutes: number): Date {
  const date = new Date(base);
  const clamped = ((Math.round(minutes) % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  date.setHours(Math.floor(clamped / 60), clamped % 60, 0, 0);
  return date;
}

function getWeeklyScheduleEntries(config: Record<string, unknown>): TimeSwitchScheduleEntry[] {
  const weeklySchedule = wireArray(config.weekly_schedule);
  if (!weeklySchedule) {
    return [];
  }

  return weeklySchedule
    .map((entry) => ({
      day: typeof field(entry, 'day') === 'number' ? clampDay(Number(field(entry, 'day'))) : undefined,
      on_time: normalizeAnchor(field(entry, 'on_time'), DEFAULT_DAILY_ON_TIME),
      off_time: normalizeAnchor(field(entry, 'off_time'), DEFAULT_DAILY_OFF_TIME),
    }))
    .filter((entry: TimeSwitchScheduleEntry) => entry.day !== undefined)
    .sort(compareEntry);
}

function expandDailyPairs(pairs: TimeSwitchSchedulePair[]): TimeSwitchScheduleEntry[] {
  return pairs.flatMap((pair) =>
    WEEKDAY_OPTIONS.map((weekday) => ({
      day: weekday.day,
      on_time: pair.on_time,
      off_time: pair.off_time,
    })),
  );
}

function toWeeklyWindow(day: number, onMinutes: number, offMinutes: number, label: string) {
  const start = day * MINUTES_PER_DAY + onMinutes;
  const end =
    offMinutes <= onMinutes ? start + (MINUTES_PER_DAY - onMinutes + offMinutes) : day * MINUTES_PER_DAY + offMinutes;

  return {
    start,
    end,
    label,
  };
}

/** Normalize a raw edge to a canonical anchor (legacy "HH:MM" → clock time). */
export function normalizeAnchor(value: unknown, fallback: string): TimeSwitchAnchor {
  if (typeof value === 'string') {
    return { type: 'time', time: /^\d{2}:\d{2}$/.test(value) ? value : fallback };
  }

  if (isTimeSwitchAnchor(value)) {
    if (value.type === 'sunrise' || value.type === 'sunset') {
      const offset = value.offset_minutes;
      return {
        type: value.type,
        offset_minutes: typeof offset === 'number' && Number.isFinite(offset) ? offset : 0,
      };
    }
    if (value.type === 'time') {
      const time = typeof value.time === 'string' && /^\d{2}:\d{2}$/.test(value.time) ? value.time : fallback;
      return { type: 'time', time };
    }
  }

  return { type: 'time', time: fallback };
}

export function defaultTimeAnchor(type: 'time' | 'sunrise' | 'sunset'): TimeSwitchAnchor {
  if (type === 'sunrise') return { type: 'sunrise', offset_minutes: 0 };
  if (type === 'sunset') return { type: 'sunset', offset_minutes: 0 };
  return { type: 'time', time: DEFAULT_DAILY_ON_TIME };
}

export function formatAnchorLabel(anchor: TimeSwitchAnchor | undefined | null): string {
  if (!anchor) return '—';
  if (anchor.type === 'time') return anchor.time || '—';
  const base = anchor.type === 'sunrise' ? tt('Sunrise', 'Sončni vzhod') : tt('Sunset', 'Sončni zahod');
  const offset = anchor.offset_minutes ?? 0;
  if (offset === 0) return base;
  return `${base} ${offset > 0 ? '+' : ''}${offset}min`;
}

/** Resolve an anchor to minutes-of-day using a reference sun set (or fallback). */
function resolveAnchorMinutes(anchor: TimeSwitchAnchor, sunTimes: SunTimesLike | null): number {
  if (anchor.type === 'time') {
    return timeToMinutes(anchor.time ?? DEFAULT_DAILY_ON_TIME);
  }
  const base =
    anchor.type === 'sunrise'
      ? sunTimes
        ? hoursToMinutes(sunTimes.sunrise)
        : FALLBACK_SUNRISE_MINUTES
      : sunTimes
        ? hoursToMinutes(sunTimes.sunset)
        : FALLBACK_SUNSET_MINUTES;
  return base + (anchor.offset_minutes ?? 0);
}

/** Resolve an anchor to a concrete Date for the given day. */
function resolveAnchorDate(anchor: TimeSwitchAnchor, dayBase: Date, sunTimes: SunTimesLike | null): Date {
  if (anchor.type === 'time') {
    return buildDate(dayBase, anchor.time ?? DEFAULT_DAILY_ON_TIME);
  }

  if (anchor.type === 'sunrise' || anchor.type === 'sunset') {
    if (sunTimes) {
      const base = anchor.type === 'sunrise' ? sunTimes.sunrise : sunTimes.sunset;
      return new Date(base.getTime() + (anchor.offset_minutes ?? 0) * 60000);
    }
    const minutes = resolveAnchorMinutes(anchor, null);
    return buildDateFromMinutes(dayBase, minutes);
  }

  return buildDate(dayBase, DEFAULT_DAILY_ON_TIME);
}

function shiftSunTimes(sunTimes: SunTimesLike, dayOffset: number): SunTimesLike {
  const sunrise = new Date(sunTimes.sunrise);
  sunrise.setDate(sunrise.getDate() + dayOffset);
  const sunset = new Date(sunTimes.sunset);
  sunset.setDate(sunset.getDate() + dayOffset);
  return { sunrise, sunset };
}

function hoursToMinutes(date: Date): number {
  return date.getHours() * 60 + date.getMinutes();
}

function normalizeTime(value: unknown, fallback: string): string {
  if (typeof value !== 'string') return fallback;
  return /^\d{2}:\d{2}$/.test(value) ? value : fallback;
}

function clampDay(day: number): number {
  if (day < 0) return 0;
  if (day > 6) return 6;
  return day;
}

function comparePair(left: TimeSwitchSchedulePair, right: TimeSwitchSchedulePair): number {
  const leftOn = resolveAnchorMinutes(normalizeAnchor(left.on_time, DEFAULT_DAILY_ON_TIME), null);
  const rightOn = resolveAnchorMinutes(normalizeAnchor(right.on_time, DEFAULT_DAILY_ON_TIME), null);
  const leftOff = resolveAnchorMinutes(normalizeAnchor(left.off_time, DEFAULT_DAILY_OFF_TIME), null);
  const rightOff = resolveAnchorMinutes(normalizeAnchor(right.off_time, DEFAULT_DAILY_OFF_TIME), null);
  return leftOn - rightOn || leftOff - rightOff;
}

function compareEntry(left: TimeSwitchScheduleEntry, right: TimeSwitchScheduleEntry): number {
  return (left.day ?? 0) - (right.day ?? 0) || comparePair(left, right);
}

function timeToMinutes(hhmm: string): number {
  const [hours, minutes] = normalizeTime(hhmm, DEFAULT_DAILY_ON_TIME).split(':').map(Number);
  return (hours || 0) * 60 + (minutes || 0);
}

function clamp01(value: number): number {
  if (value < 0) return 0;
  if (value > 1) return 1;
  return value;
}

function getStateValue(currentValues: Record<string, unknown>, automationType: string, stateKey: string): unknown {
  return currentValues[prefixedKey(automationType, stateKey)];
}

function getBooleanState(currentValues: Record<string, unknown>, automationType: string, stateKey: string): boolean {
  const value = getStateValue(currentValues, automationType, stateKey);
  return value === true || value === 1 || value === 'true';
}

function getNumberState(
  currentValues: Record<string, unknown>,
  automationType: string,
  stateKey: string,
): number | null {
  const value = getStateValue(currentValues, automationType, stateKey);
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function getNullableBooleanState(
  currentValues: Record<string, unknown>,
  automationType: string,
  stateKey: string,
): boolean | null {
  const value = getStateValue(currentValues, automationType, stateKey);
  if (value === undefined || value === null || value === '') return null;
  return getBooleanState(currentValues, automationType, stateKey);
}

function getStringState(
  currentValues: Record<string, unknown>,
  automationType: string,
  stateKey: string,
): string | null {
  const value = getStateValue(currentValues, automationType, stateKey);
  return typeof value === 'string' && value.length > 0 ? value : null;
}
